// Public CSAT survey page — reachable without authentication.
//
// Loaded from the link emailed to the submitter when their ticket is
// resolved. Validates the (ticketId, token) pair via the public
// csatVerify HTTP endpoint, then accepts a 1-5 rating + optional
// comment via the submitCsat callable.

import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getFunctions, httpsCallable } from "firebase/functions";
import { useBranding } from "@/lib/branding";
import { Spinner } from "@/components/shared";

type VerifyState =
  | { kind: "loading" }
  | { kind: "valid"; ticketTitle: string; priority?: string }
  | { kind: "invalid"; reason: string };

const RATING_LABELS: Record<number, { emoji: string; label: string }> = {
  1: { emoji: "😡", label: "Terrible" },
  2: { emoji: "🙁", label: "Poor" },
  3: { emoji: "😐", label: "Okay" },
  4: { emoji: "🙂", label: "Good" },
  5: { emoji: "🤩", label: "Excellent" },
};

function reasonText(reason: string): string {
  switch (reason) {
    case "already_used":
      return "This survey has already been submitted. Thanks for your feedback!";
    case "not_found":
      return "We couldn't find that ticket. The link may be from a deleted ticket.";
    case "invalid_token":
      return "This survey link is invalid or has expired.";
    case "missing_params":
      return "This link is missing information. Try copying the full URL from your email.";
    default:
      return "We couldn't open this survey. Please try again later.";
  }
}

export default function CsatSurvey() {
  const branding = useBranding();
  const [params] = useSearchParams();
  const ticketId = params.get("ticket") || "";
  const token = params.get("token") || "";

  const [state, setState] = useState<VerifyState>({ kind: "loading" });
  const [score, setScore] = useState<number | null>(null);
  const [hover, setHover] = useState<number | null>(null);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    document.documentElement.style.setProperty("--blue", branding.primaryColor);
    document.title = `Rate your ticket — ${branding.portalName}`;
  }, [branding.primaryColor, branding.portalName]);

  // Verify the link on mount via the public callable. Avoids making
  // the user choose a rating only to discover the link is dead.
  useEffect(() => {
    if (!ticketId || !token) {
      setState({ kind: "invalid", reason: "missing_params" });
      return;
    }
    const fn = httpsCallable<unknown, { valid: boolean; reason?: string; ticketTitle?: string; priority?: string }>(getFunctions(), "csatVerify");
    fn({ ticketId, token })
      .then((res) => {
        const data = res.data;
        if (data.valid) {
          setState({ kind: "valid", ticketTitle: data.ticketTitle ?? "", priority: data.priority });
        } else {
          setState({ kind: "invalid", reason: data.reason || "unknown" });
        }
      })
      .catch(() => setState({ kind: "invalid", reason: "server_error" }));
  }, [ticketId, token]);

  const submit = async () => {
    if (score === null) return;
    setSubmitting(true);
    setError("");
    try {
      const fn = httpsCallable(getFunctions(), "submitCsat");
      await fn({ ticketId, token, score, comment: comment.trim() });
      setSubmitted(true);
    } catch (e) {
      const msg = (e as { message?: string }).message || "Something went wrong.";
      setError(msg);
    }
    setSubmitting(false);
  };

  const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div style={{
      minHeight: "100vh", background: "var(--bg)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div className="card" style={{ width: "100%", maxWidth: 500, padding: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          {branding.logoUrl ? (
            <img src={branding.logoUrl} alt={branding.portalName} style={{ height: 30, maxWidth: 130, objectFit: "contain" }} />
          ) : (
            <div style={{ width: 32, height: 32, background: "var(--blue)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
            </div>
          )}
          <div style={{ fontWeight: 600, fontSize: 15 }}>{branding.portalName}</div>
        </div>
        {children}
      </div>
    </div>
  );

  if (state.kind === "loading") {
    return (
      <Wrapper>
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spinner size={28} />
        </div>
      </Wrapper>
    );
  }

  if (state.kind === "invalid") {
    return (
      <Wrapper>
        <h2 style={{ marginBottom: 8 }}>Survey unavailable</h2>
        <p style={{ color: "var(--text2)", fontSize: 14 }}>{reasonText(state.reason)}</p>
      </Wrapper>
    );
  }

  if (submitted) {
    return (
      <Wrapper>
        <div style={{ textAlign: "center", padding: "16px 0" }}>
          <div style={{ fontSize: 56, marginBottom: 14 }}>🙏</div>
          <h2 style={{ marginBottom: 8 }}>Thanks for the feedback!</h2>
          <p style={{ color: "var(--text2)", fontSize: 14, lineHeight: 1.6 }}>
            Your rating helps us improve. You can close this tab.
          </p>
        </div>
      </Wrapper>
    );
  }

  // Valid + not yet submitted
  return (
    <Wrapper>
      <h2 style={{ marginBottom: 6 }}>How did we do?</h2>
      <p style={{ color: "var(--text2)", fontSize: 13, marginBottom: 20 }}>
        Rate your experience resolving:<br/>
        <span style={{ color: "var(--text)", fontWeight: 500 }}>{state.ticketTitle}</span>
      </p>

      <div style={{ display: "flex", gap: 8, justifyContent: "space-between", marginBottom: 18 }}>
        {[1, 2, 3, 4, 5].map((v) => {
          const active = score !== null && score >= v;
          const showHover = hover !== null && hover >= v && score === null;
          const lit = active || showHover;
          return (
            <button
              key={v}
              type="button"
              onClick={() => setScore(v)}
              onMouseEnter={() => setHover(v)}
              onMouseLeave={() => setHover(null)}
              aria-label={`${v} out of 5`}
              style={{
                flex: 1, padding: "14px 6px",
                border: `1.5px solid ${score === v ? "var(--blue)" : "var(--border-med)"}`,
                background: score === v ? "var(--blue-l)" : "var(--surface)",
                borderRadius: "var(--rs)",
                cursor: "pointer",
                fontFamily: "var(--font)",
                fontSize: 13,
                color: "var(--text)",
                transition: "all .15s",
                opacity: lit ? 1 : 0.55,
              }}
            >
              <div style={{ fontSize: 26, marginBottom: 4 }}>{RATING_LABELS[v].emoji}</div>
              <div style={{ fontSize: 11, fontWeight: 500, color: "var(--text2)" }}>{RATING_LABELS[v].label}</div>
            </button>
          );
        })}
      </div>

      <div className="fg" style={{ marginBottom: 18 }}>
        <label>Anything you'd like to add? <span style={{ color: "var(--text2)", fontWeight: 400 }}>(optional)</span></label>
        <textarea className="fta" rows={4}
          placeholder="What worked well, what didn't, anything we should know."
          value={comment} onChange={(e) => setComment(e.target.value)}
          maxLength={2000}
        />
      </div>

      {error && (
        <div style={{ padding: "10px 12px", background: "var(--red-l)", color: "var(--red-t)", borderRadius: "var(--rs)", fontSize: 13, marginBottom: 12 }}>
          {error}
        </div>
      )}

      <button
        className="btn btn-primary"
        style={{ width: "100%", justifyContent: "center", padding: "10px 14px", fontSize: 14 }}
        onClick={submit}
        disabled={score === null || submitting}
      >
        {submitting ? <Spinner size={14} color="white" /> : "Submit feedback"}
      </button>
    </Wrapper>
  );
}
